import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { MessageSquare, Loader2, Search } from 'lucide-react';
import { ChatBubble, ChatComposer, ImageLightbox } from '../chat/ChatPrimitives';

const previewLast = (m) => {
  if (!m) return null;
  if (m.content) return m.content;
  const n = (m.attachments || []).length;
  if (n > 0) return `📎 ${n} attachment${n === 1 ? '' : 's'}`;
  return null;
};

const MessagesTab = () => {
  const { toast } = useToast();
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const bottomRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try { const { data } = await api.get('/admin/messages/threads'); setThreads(data); }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadThreads(); const iv = setInterval(loadThreads, 15000); return () => clearInterval(iv); }, [loadThreads]);

  const loadThread = useCallback(async (recruiter_id) => {
    try {
      const { data } = await api.get(`/admin/messages/thread/${recruiter_id}`);
      setMessages(data);
      await api.post(`/admin/messages/thread/${recruiter_id}/read`);
      loadThreads();
    } catch {}
  }, [loadThreads]);

  useEffect(() => {
    if (!selected) return;
    loadThread(selected.recruiter_id);
    const iv = setInterval(() => loadThread(selected.recruiter_id), 8000);
    return () => clearInterval(iv);
  }, [selected, loadThread]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const send = async ({ content, files }) => {
    if (!selected) return false;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('content', content || '');
      (files || []).forEach((f) => fd.append('attachments', f));
      await api.post(`/admin/messages/thread/${selected.recruiter_id}/send`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await loadThread(selected.recruiter_id);
      return true;
    } catch (err) {
      toast({ title: 'Send failed', description: err?.response?.data?.detail || 'Error' });
      return false;
    } finally { setSending(false); }
  };

  const q = filter.toLowerCase();
  const threadFiltered = threads.filter((t) =>
    !q || (t.recruiter_name || '').toLowerCase().includes(q) || (t.recruiter_email || '').toLowerCase().includes(q)
  );

  return (
    <div className="bg-white border border-[#0B5E2A]/10 rounded-xl overflow-hidden grid grid-cols-1 md:grid-cols-[320px_1fr] h-[70vh] min-h-[520px]">
      {/* Threads list */}
      <aside className="border-r border-[#0B5E2A]/10 flex flex-col min-h-0">
        <div className="p-3 border-b border-[#0B5E2A]/10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6b7e6d]" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search recruiter..." className="w-full pl-9 pr-3 py-2 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (<div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#6b7e6d]" /></div>)
          : threadFiltered.length === 0 ? (<div className="p-8 text-center text-sm text-[#6b7e6d]">No recruiters yet.</div>)
          : threadFiltered.map((t) => {
            const active = selected?.recruiter_id === t.recruiter_id;
            const last = previewLast(t.last_message);
            return (
              <button key={t.recruiter_id} onClick={() => setSelected(t)} data-testid={`admin-thread-${t.recruiter_id}`} className={`w-full text-left px-4 py-3 border-b border-[#0B5E2A]/5 hover:bg-[#f6faf3] transition-colors ${active ? 'bg-[#f0f6ee]' : ''}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-[#0B5E2A] text-white flex items-center justify-center font-semibold text-sm shrink-0">{(t.recruiter_name || '?').slice(0, 1).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-[#0B5E2A] truncate text-sm">{t.recruiter_name}</div>
                      {t.unread_admin > 0 && (<span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-[#F0B93C] text-[#06401d] text-[11px] font-bold">{t.unread_admin}</span>)}
                    </div>
                    <div className="text-xs text-[#6b7e6d] truncate mt-0.5">
                      {last ? (<span>{t.last_message?.sender_role === 'admin' ? 'You: ' : ''}{last}</span>) : (<span className="italic">No messages yet</span>)}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Conversation */}
      <section className="flex flex-col min-h-0">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-[#6b7e6d]">
              <MessageSquare className="h-10 w-10 mx-auto brand-green opacity-60" />
              <div className="mt-3 font-display text-lg text-[#0B5E2A]">Select a recruiter</div>
              <p className="mt-1 text-sm">Pick a conversation from the left to view the chat.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-[#0B5E2A]/10 flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-[#0B5E2A] text-white flex items-center justify-center font-semibold text-sm">{(selected.recruiter_name || '?').slice(0,1).toUpperCase()}</div>
              <div>
                <div className="font-semibold text-[#0B5E2A]">{selected.recruiter_name}</div>
                <div className="text-xs text-[#6b7e6d]">{selected.recruiter_email} · {selected.recruiter_company || ''}</div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-[#faf9f2]" data-testid="admin-chat-window">
              {messages.length === 0 && (<div className="text-center text-sm text-[#6b7e6d] py-10">No messages yet. Start the conversation!</div>)}
              {messages.map((m) => (
                <ChatBubble
                  key={m.id}
                  message={m}
                  mine={m.sender_role === 'admin'}
                  readMark={true}
                  attachmentUrl={(mid, idx) => `/admin/messages/${mid}/attachments/${idx}`}
                  onImageOpen={(src, name) => setLightbox({ src, name })}
                />
              ))}
              <div ref={bottomRef} />
            </div>
            <ChatComposer onSend={send} sending={sending} placeholder="Type a message..." />
          </>
        )}
      </section>
      {lightbox && <ImageLightbox src={lightbox.src} name={lightbox.name} onClose={() => setLightbox(null)} />}
    </div>
  );
};

export default MessagesTab;
